import { z } from 'zod';
/**
 * Format: YYYY-MM-dd
 */
const dateStringSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Must be in YYYY-MM-DD format' })
    .date();
const base10NumberSchema = z.string().regex(/^\d+$/, {
    message: 'Must be a base10 string (digits only)',
});
export const ansiHeaderDataSchema = z.object({
    /**
     * Issuer Identification Number  6 bytes   6 digit number - Bay Area 234567 CO IMS 345678
     */
    badgeIssuerId: base10NumberSchema.length(6),
    /**
     * AAMVA Version Number          2 bytes   03      2005 standard I am working from
     */
    aamvaVersionMajor: base10NumberSchema.length(2),
    /**
     * Jurisdiction Version Number   2 bytes   00      this is the revision of the AAMVA version, 00 should work
     */
    aamvaVersionMinor: base10NumberSchema.length(2),
    /**
     * Number of Entries             2 bytes   01      the number of subfiles contained in the barcode
     */
    aamvaSubfilesCount: base10NumberSchema.length(2),
});
/**
 * According to AAMVA standard
 */
export const barcodeAamvaDataSchema = z.object({
    /**
     * ANSI Header static data
     * Header itself can only be calculated during conversion to raw AAMVA string
     */
    ansiHeaderData: ansiHeaderDataSchema,
    /**
     * DAJ	State		Specification has format of F2A, but we support spell the state name out, e.g. Georgia
     */
    dajState: z.string(),
    /**
     * DAK	Zip/Postal Code	F11ANS	NNNNN-NNNN
     */
    dakZipCode: z.string().optional(),
    /**
     * DAQ	Badge Number	V25ANS	See https://confluence.juvare.com/display/SE/Badge+Barcodes+Addendum
     */
    daqBadgeNumber: z.string().length(25),
    /**
     * DAY	Eye Color	F3A	(Not used in Michigan)
     */
    dayEyeColor: z
        .enum([
        'BLK', //	Black
        'BLU', //	Blue
        'GRY', //	Gray
        'RED', //	Red
        'WHT', //	White
        'YEL', //	Yellow
        'UNK', //	Unknown
    ])
        .optional(),
    /**
     * DBA	Expiration Date	F10NS	Specification has format of F8N, but we have format of YYYY-MM-DD
     */
    dbaExpirationDate: dateStringSchema,
    /**
     * DBB	Date of Birth	F10NS	Specification has format of F8N, but we have format of YYYY-MM-DD
     */
    dbbDateOfBirth: dateStringSchema.optional(), // YYYY-MM-dd
    /**
     * DBC	Gender	F1N	1 = male, 2 = female, 9 = not specified. (Not used in Michigan)
     */
    dbcGender: z.enum(['1', '2', '9']).optional(),
    /**
     * DBD	Issue Date	F10NS	Specification has format of F8N, but we have format of YYYY-MM-DD
     */
    dbdIssueDate: dateStringSchema,
    /**
     * DCF	Document Discriminator	V25ANS	001, 002, 0003...
     */
    dcfRevisionNumber: base10NumberSchema.length(3),
    /**
     * DCG	Country		Specification has format of F3A, but we spell the country name out, e.g. United States of America
     */
    dcgCountry: z.string(),
    /**
     * DCS	Family Name	VxxANS	Last Name (Not in specification)
     */
    dcsLastName: z.string(),
    /**
     * DCT	Given Name	VxxANS	First Name + space + Middle Name (if present) [Not in specification]
     */
    dctFirstAndMiddleName: z.string(),
    /**
     * ZCE	Affiliation [Not in specification]
     */
    zceAffiliation: z.string(),
    /**
     * ZCT	Occupation [Not in specification]
     */
    zctOccupationTitle: z.string().optional(),
    zclAccessLevel: z.string().optional(), // custom, not part of spec
    /**
     * DAU	Height	F6ANS	Non-leading zero inches, e.g. "073 in" or "181 cm" (Not used in Michigan nor Massachusetts)
     */
    dauHeight: z.string().length(6).optional(),
    /**
     * DAI	City	V20ANS
     */
    daiCity: z.string().optional(),
});
